/**
 * Portable jewelry PDP kit: custom variant picker (extends theme VariantRadiosDetail),
 * vendor title sync, size-guide modal, ring size label sync.
 * Requires theme.js first (sets window.JewelryPickerVariantRadiosDetail).
 */
(function () {
  'use strict';

  var Base = window.JewelryPickerVariantRadiosDetail;
  if (!Base) {
    console.warn(
      '[custom-jewellery] JewelryPickerVariantRadiosDetail missing; load theme.js before custom-jewellery.js.'
    );
    return;
  }

  class CustomJewelryVariantPicker extends Base {
    init() {}

    static TAB_DEFS() {
      return [
        ['silver', 'Silver'],
        ['10k', '10K Gold'],
        ['14k', '14K Gold'],
        ['18k', '18K Gold'],
        ['platinum', 'Platinum'],
      ];
    }

    connectedCallback() {
      if (this._cjvReady) return;
      const cfgEl = this.querySelector('[data-cjv-config]');
      if (!cfgEl) return;
      this._cjvReady = true;
      let cfg;
      try {
        cfg = JSON.parse(cfgEl.textContent);
      } catch (e) {
        return;
      }
      this.variantData = cfg.variants || [];
      this.metalTabs = cfg.metalTabs || [];
      this.cjvColors = cfg.colors || [];
      this.metalOptionIndex =
        typeof cfg.metalOptionIndex === 'number' ? cfg.metalOptionIndex : 0;
      this.skipOptionPositions = new Set(cfg.skipOptionPositions || []);
      this.preservedSibling = {};
      if (!this.variantData.length) return;

      this.addEventListener('change', this.onVariantChange);

      this.tabConfig = [];
      for (const [key, label] of CustomJewelryVariantPicker.TAB_DEFS()) {
        const metals = [
          ...new Set(
            this.metalTabs.filter((m) => m.tab === key).map((m) => m.metal)
          ),
        ];
        if (metals.length) {
          metals.sort((a, b) => a.localeCompare(b));
          this.tabConfig.push({ key, label, metals });
        }
      }

      const initial = this.getInitialVariant();
      const initialMetal = initial.options[this.metalOptionIndex];
      this.selectedTabKey = this.tabKeyForMetal(initialMetal);
      this.selectedMetalValue = initialMetal;
      this.selectedColor =
        this.selectedTabKey === 'platinum'
          ? null
          : this.cjvColors[0] || null;

      this.seedPreservedFromVariant(initial);

      this.renderCategoryTabs();
      this.bindSecondary();

      this.addEventListener('click', this.onContainerClick.bind(this));

      this.renderSwatches();
      this.syncFromDom();
    }

    getInitialVariant() {
      const params = new URLSearchParams(window.location.search);
      const vid = params.get('variant');
      return (
        this.variantData.find((v) => String(v.id) === vid) || this.variantData[0]
      );
    }

    seedPreservedFromVariant(v) {
      if (!v?.options) return;
      const count = v.options.length;
      for (let i = 1; i <= count; i++) {
        if (i - 1 !== this.metalOptionIndex) {
          this.preservedSibling[i] = v[`option${i}`];
        }
      }
    }

    tabKeyForMetal(metalVal) {
      const row = this.metalTabs.find((m) => m.metal === metalVal);
      return row ? row.tab : 'silver';
    }

    metalsInTab(tabKey) {
      const t = this.tabConfig.find((x) => x.key === tabKey);
      return t ? t.metals : [];
    }

    getVariantData() {
      if (!this.variantData || !this.variantData.length) {
        const cfgEl = this.querySelector('[data-cjv-config]');
        if (cfgEl) {
          try {
            const cfg = JSON.parse(cfgEl.textContent);
            this.variantData = cfg.variants || [];
            this.metalTabs = cfg.metalTabs || this.metalTabs || [];
          } catch (e) {
            this.variantData = [];
          }
        } else {
          this.variantData = [];
        }
      }
      return this.variantData;
    }

    applyColourToButton(btn, col) {
      if (!col) return;
      const hex = col.hex;
      const rgb = col.rgb;
      if (hex) {
        btn.style.background = `linear-gradient(145deg, ${hex}, ${hex})`;
        return;
      }
      if (rgb) {
        btn.style.background = `linear-gradient(145deg, ${rgb}, ${rgb})`;
      }
    }

    colourStyleLabel(col) {
      const h = `${col.hex || ''} ${col.rgb || ''} ${col.label || ''}`.toLowerCase();
      if (h.includes('rose')) return 'cjv-grad--rose';
      if (h.includes('yellow')) return 'cjv-grad--yellow';
      if (h.includes('white')) return 'cjv-grad--white';
      return '';
    }

    renderCategoryTabs() {
      const wrap = this.querySelector('.cjv-category-tabs');
      if (!wrap) return;
      wrap.innerHTML = '';
      this.tabConfig.forEach((t) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'cjv-tab';
        b.dataset.tab = t.key;
        b.textContent = t.label;
        b.setAttribute('role', 'tab');
        b.setAttribute(
          'aria-selected',
          t.key === this.selectedTabKey ? 'true' : 'false'
        );
        if (t.key === this.selectedTabKey) b.classList.add('is-active');
        wrap.appendChild(b);
      });
    }

    secondaryValueLabelForFieldset(fs) {
      if (!fs) return null;
      let lab = fs.querySelector('.cjv-secondary-value');
      if (lab) return lab;
      const diamondWrap = fs.closest('.dss-block');
      if (diamondWrap) {
        lab = diamondWrap.querySelector('.dss-header .cjv-secondary-value');
        if (lab) return lab;
      }
      return null;
    }

    bindSecondary() {
      const picker = this;
      const bindFs = (fs) => {
        if (fs._cjvSecondaryBound) return;
        fs._cjvSecondaryBound = true;
        fs.addEventListener('change', () => {
          const checked = fs.querySelector('input[type="radio"]:checked');
          const lab = picker.secondaryValueLabelForFieldset(fs);
          if (checked && lab) lab.textContent = checked.value;
          if (!picker.contains(fs)) {
            picker.syncFromDom();
          }
        });
      };
      this.querySelectorAll('[data-secondary-option]').forEach(bindFs);
      const root = this.closest('.product__item-js');
      if (root) {
        root.querySelectorAll('[data-secondary-option]').forEach((fs) => {
          if (!this.contains(fs)) bindFs(fs);
        });
      }
    }

    secondaryFieldsetForPos(pos) {
      const within = this.querySelector(`[data-secondary-option="${pos}"]`);
      if (within) return within;
      const root = this.productTarget || this.closest('.product__item-js');
      return root ? root.querySelector(`[data-secondary-option="${pos}"]`) : null;
    }

    syncSecondaryUIFromVariant() {
      if (!this.currentVariant) return;
      const count = this.variantData[0]?.options?.length || 0;
      for (let pos = 1; pos <= count; pos++) {
        if (pos - 1 === this.metalOptionIndex) continue;
        const fs = this.secondaryFieldsetForPos(pos);
        if (!fs) continue;
        const val = this.currentVariant[`option${pos}`];
        if (val == null) continue;
        const lab = this.secondaryValueLabelForFieldset(fs);
        if (lab) lab.textContent = val;
        fs.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.checked = input.value === val;
        });
      }
    }

    onContainerClick(e) {
      const tab = e.target.closest('.cjv-tab');
      if (tab) {
        e.preventDefault();
        const key = tab.dataset.tab;
        if (!key || key === this.selectedTabKey) return;
        this.selectedTabKey = key;
        this.querySelectorAll('.cjv-tab').forEach((b) => {
          b.classList.toggle('is-active', b.dataset.tab === key);
          b.setAttribute(
            'aria-selected',
            b.dataset.tab === key ? 'true' : 'false'
          );
        });
        const metals = this.metalsInTab(key);
        this.selectedMetalValue = metals[0] || this.selectedMetalValue;
        if (key === 'platinum') this.selectedColor = null;
        else if (!this.selectedColor && this.cjvColors.length)
          this.selectedColor = this.cjvColors[0];
        this.renderSwatches();
        this.syncFromDom();
        return;
      }

      const sw = e.target.closest('.cjv-swatch');
      if (sw) {
        e.preventDefault();
        const metal = sw.dataset.metal;
        const colorIdx = sw.dataset.colorIdx;
        this.selectedMetalValue = metal;
        this.selectedTabKey = this.tabKeyForMetal(metal);
        if (this.selectedTabKey !== 'platinum' && colorIdx !== '') {
          const col = this.cjvColors.find((c) => String(c.i) === String(colorIdx));
          this.selectedColor = col || null;
        } else {
          this.selectedColor = null;
        }
        this.querySelectorAll('.cjv-swatch').forEach((s) =>
          s.classList.toggle('is-active', s === sw)
        );
        this.querySelectorAll('.cjv-tab').forEach((b) => {
          b.classList.toggle('is-active', b.dataset.tab === this.selectedTabKey);
          b.setAttribute(
            'aria-selected',
            b.dataset.tab === this.selectedTabKey ? 'true' : 'false'
          );
        });
        this.syncFromDom();
      }
    }

    renderSwatches() {
      const outer = this.querySelector('.cjv-swatches-wrap');
      const box = this.querySelector('.cjv-swatches');
      if (!outer || !box) return;
      const metals = this.metalsInTab(this.selectedTabKey);
      if (!metals.length) {
        outer.hidden = true;
        box.innerHTML = '';
        return;
      }
      const isPt = this.selectedTabKey === 'platinum';
      if (isPt) {
        outer.hidden = true;
        box.innerHTML = '';
        return;
      }
      outer.hidden = false;
      box.innerHTML = '';
      const colors = this.cjvColors.length ? this.cjvColors : [null];

      metals.forEach((metal) => {
        colors.forEach((col) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'cjv-swatch';
          btn.dataset.metal = metal;
          if (col) {
            btn.dataset.colorIdx = String(col.i);
            const grad = this.colourStyleLabel(col);
            if (grad) btn.classList.add(grad);
            else this.applyColourToButton(btn, col);
          } else {
            btn.classList.add('cjv-grad--neutral');
          }
          const a11y =
            col && col.label
              ? `${metal} ${col.label}`.trim()
              : `${metal}`.trim();
          btn.setAttribute('aria-label', a11y || 'Option');

          const metalMatch = metal === this.selectedMetalValue;
          let colorMatch = false;
          if (!col) {
            colorMatch = !this.cjvColors.length;
          } else if (
            this.selectedColor &&
            String(this.selectedColor.i) === String(col.i)
          ) {
            colorMatch = true;
          }
          if (metalMatch && colorMatch) btn.classList.add('is-active');

          box.appendChild(btn);
        });
      });
    }

    updateOptions() {
      const count = this.variantData[0]?.options?.length || 0;
      const refVariant = this.currentVariant || this.getInitialVariant();
      this.options = [];
      for (let i = 0; i < count; i++) {
        const pos = i + 1;
        if (i === this.metalOptionIndex) {
          this.options.push(this.selectedMetalValue);
        } else {
          const fs = this.secondaryFieldsetForPos(pos);
          if (fs) {
            const r = fs.querySelector('input[type="radio"]:checked');
            this.options.push(r?.value ?? refVariant[`option${pos}`]);
          } else if (this.skipOptionPositions.has(pos)) {
            this.options.push(
              this.preservedSibling[pos] ?? refVariant[`option${pos}`]
            );
          } else {
            this.options.push(refVariant[`option${pos}`]);
          }
        }
      }
    }

    updateVariantStatuses() {}

    syncFromDom() {
      this.productTarget = this.closest('.product__item-js');
      if (!this.productTarget || !this.variantData.length) return;
      const qtyEl = this.productTarget.querySelector('.productVariantsQty');
      if (!qtyEl) return;
      const variantQtyData = JSON.parse(qtyEl.textContent);
      this.groupFunctionsInit();
      if (!this.currentVariant) {
        this.groupFunctionsUnavailable(null);
      } else {
        this.groupFunctionsAvailable(variantQtyData, null);
      }
      const count = this.variantData[0]?.options?.length || 0;
      if (this.currentVariant) {
        for (let i = 1; i <= count; i++) {
          if (i - 1 !== this.metalOptionIndex) {
            this.preservedSibling[i] = this.currentVariant[`option${i}`];
          }
        }
      }
      this.updateLabel();
      this.syncSecondaryUIFromVariant();
      this.updateColorProperty();
      const prefix = (this.dataset.headingDisplay || 'Precious Metal:').trim();
      const vt = document.getElementById('vendor-variant-title');
      if (vt) vt.textContent = `${prefix} ${this.getValuePart()}`.trim();
    }

    formatMetalForDisplay(raw) {
      if (!raw) return '';
      let m = String(raw).trim();
      m = m.replace(/\b(\d+)\s*k\b/gi, (_, d) => `${d}K`);
      return m
        .split(/\s+/)
        .map((w) => {
          if (/^\d+K$/i.test(w)) return `${w.slice(0, -1)}K`;
          if (/^\d+$/.test(w)) return w;
          if (!w) return w;
          return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
        })
        .join(' ');
    }

    formatColorLabel(raw) {
      if (!raw) return '';
      return String(raw)
        .trim()
        .split(/\s+/)
        .map((w) => {
          if (!w) return w;
          return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
        })
        .join(' ');
    }

    getValuePart() {
      const m = this.formatMetalForDisplay(this.selectedMetalValue);
      if (this.selectedTabKey === 'platinum' || !this.selectedColor) return m;
      const c = this.formatColorLabel(this.selectedColor.label);
      return `${m} ${c}`.trim();
    }

    updateLabel() {
      const el = this.querySelector('.cjv-current-label');
      if (el) el.textContent = this.getValuePart();
    }

    updateColorProperty() {
      if (this.dataset.addColorProperty !== 'true') return;
      const form = document.getElementById(`product-form-${this.dataset.section}`);
      if (!form) return;
      const propName = this.dataset.colorPropertyName || 'Metal color';
      const fullName = `properties[${propName}]`;
      let inputEl = Array.from(form.elements).find(
        (el) => el.tagName === 'INPUT' && el.name === fullName
      );
      if (!inputEl) {
        inputEl = document.createElement('input');
        inputEl.type = 'hidden';
        inputEl.name = fullName;
        form.appendChild(inputEl);
      }
      if (this.selectedTabKey === 'platinum' || !this.selectedColor) {
        inputEl.value = '';
      } else {
        inputEl.value = this.selectedColor.label || '';
      }
    }
  }

  if (!customElements.get('custom-jewelry-variant-picker')) {
    customElements.define('custom-jewelry-variant-picker', CustomJewelryVariantPicker);
  }

  document.addEventListener('DOMContentLoaded', function () {
    var vendorVariantTitle = document.getElementById('vendor-variant-title');
    if (!vendorVariantTitle) return;

    function updateTitle() {
      var picker = document.querySelector('custom-jewelry-variant-picker');
      if (picker) {
        var pfx = (picker.dataset.headingDisplay || 'Precious Metal:').trim();
        var val = picker.querySelector('.cjv-current-label');
        var body = val ? val.textContent.trim() : '';
        vendorVariantTitle.textContent = (body ? pfx + ' ' + body : pfx)
          .replace(/\s+/g, ' ')
          .trim();
        return;
      }

      var colorEl = document.querySelector('.product-form__input_color .option_value');

      if (!colorEl) {
        colorEl = document.querySelector('.product-form__input .option_value');
      }

      if (colorEl) {
        vendorVariantTitle.textContent = colorEl.textContent.trim();
      }
    }

    updateTitle();

    var observer = new MutationObserver(updateTitle);
    document.querySelectorAll('.option_value').forEach(function (el) {
      observer.observe(el, { childList: true, subtree: true, characterData: true });
    });
    var cjvPicker = document.querySelector('custom-jewelry-variant-picker');
    if (cjvPicker) {
      var cjvLabel = cjvPicker.querySelector('.cjv-current-label');
      if (cjvLabel) {
        observer.observe(cjvLabel, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }
    }
  });

  document.addEventListener('DOMContentLoaded', function () {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a.dss-size-guide--modal');
      if (!link) return;
      e.preventDefault();
      var href = link.getAttribute('href');
      if (!href) return;

      if (typeof tingle === 'undefined') {
        window.location.href = href;
        return;
      }

      function dssStripScripts(root) {
        if (!root) return;
        root.querySelectorAll('script').forEach(function (s) {
          s.remove();
        });
      }

      function dssBuildGuideSurface(doc) {
        var root =
          doc.querySelector('#MainContent') ||
          doc.querySelector('main#main') ||
          doc.querySelector('main') ||
          doc.body;

        var wrap = document.createElement('div');
        wrap.className = 'dss-guide-surface';

        var rte =
          root.querySelector('.page-default .rte') ||
          root.querySelector('.shopify-policy__container .rte') ||
          root.querySelector('#MainContent .rte') ||
          root.querySelector('.rte');

        var titleEl = root.querySelector('.page-default .page-title h1');
        if (!titleEl) {
          titleEl =
            root.querySelector('.page-title h1') ||
            root.querySelector('.article-header h1');
        }

        var showHeading =
          titleEl &&
          rte &&
          !rte.contains(titleEl) &&
          titleEl.textContent.trim();

        if (showHeading) {
          var h2 = document.createElement('h2');
          h2.className = 'dss-guide-surface__title';
          h2.textContent = titleEl.textContent.trim();
          wrap.appendChild(h2);
        }

        if (rte && rte.innerHTML.trim()) {
          var body = document.createElement('div');
          body.className = 'dss-guide-surface__body';
          body.innerHTML = rte.innerHTML;
          wrap.appendChild(body);
        } else if (root && root.innerHTML.trim()) {
          var fb = document.createElement('div');
          fb.className = 'dss-guide-surface__body dss-guide-surface__body--fallback';
          fb.innerHTML = root.innerHTML;
          wrap.appendChild(fb);
        }

        dssStripScripts(wrap);
        return wrap;
      }

      var modal = new tingle.modal({
        footer: false,
        stickyFooter: false,
        closeMethods: ['overlay', 'button', 'escape'],
        closeLabel: 'Close',
        cssClass: ['dss-guide-modal'],
        onOpen: function () {
          document.documentElement.classList.add('open-popup');
          if (typeof getScrollBarWidth !== 'undefined' && getScrollBarWidth.init) {
            document.documentElement.style.setProperty(
              'padding-right',
              getScrollBarWidth.init() + 'px'
            );
          }
        },
        onClose: function () {
          document.documentElement.classList.remove('open-popup');
          document.documentElement.style.removeProperty('padding-right');
        },
        beforeClose: function () {
          return true;
        },
      });

      var loading = document.createElement('div');
      loading.className = 'dss-guide-loading';
      loading.textContent = 'Loading…';
      modal.setContent(loading);
      modal.open();

      var fetchUrl = href;
      try {
        fetchUrl = new URL(href, window.location.href).toString();
      } catch (err) {}

      fetch(fetchUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var surface = dssBuildGuideSurface(doc);
          modal.setContent(surface);
        })
        .catch(function () {
          var err = document.createElement('div');
          err.className = 'dss-guide-error';
          var p1 = document.createElement('p');
          p1.textContent = 'Unable to load this page in the pop-up.';
          var p2 = document.createElement('p');
          var a = document.createElement('a');
          a.href = href;
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          a.textContent = 'Open size guide in a new tab';
          p2.appendChild(a);
          err.appendChild(p1);
          err.appendChild(p2);
          modal.setContent(err);
        });
    });
  });

  function syncRingSizeUsDisplay(select) {
    if (!select || !select.matches || !select.matches('.rs-us-select')) return;
    var block = select.closest('.rs-us-block');
    if (!block) return;
    var el = block.querySelector('.rs-us-current-value');
    if (!el) return;
    var v = select.value;
    el.textContent = v || '';
  }

  document.body.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.matches && t.matches('.rs-us-select')) {
      syncRingSizeUsDisplay(t);
    }
  });

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.rs-us-select').forEach(syncRingSizeUsDisplay);
  });
})();
